import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings, Languages } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';
/**
 * GA4LanguagesChart - Composant pour afficher les metriques par langue GA4
 * Langues: Francais, Anglais, Espagnol, etc.
 */
const GA4LanguagesChart = ({
  languages = [],
  previousLanguages = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  conversionLabel = 'Conversions',
  serverTotals = null,
  serverPreviousTotals = null,
  // Props pour la configuration des metriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_languages',
  // Props pour les options avancees (conversions)
  token = null,
  propertyId = null,
  conversionConfig = null,
  onConversionConfigSave = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  /*
   * Dix lignes à l'écran, le reste à la page suivante — comme les Canaux et la Géographie, qui
   * paginaient déjà. Ce tableau affichait tout, et la traîne occupait plusieurs écrans.
   */
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les metriques du composant (reutilise ga4_devices pour l'instant)
  const componentMetrics = useComponentMetrics('ga4_devices', accountId);

  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  const metricsOrder = componentMetrics.order || componentMetrics.metrics || [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28'];


  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedLanguages = useMemo(() => {
    if (!languages || !languages.length) return [];

    const sorted = [...languages];
    sorted.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'language') {
        aValue = a.language || '';
        bValue = b.language || '';
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortConfig.key === 'conversionRate') {
        aValue = a.sessions > 0 ? (a.conversions / a.sessions * 100) : 0;
        bValue = b.sessions > 0 ? (b.conversions / b.sessions * 100) : 0;
      } else {
        aValue = Number(a[sortConfig.key]) || 0;
        bValue = Number(b[sortConfig.key]) || 0;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [languages, sortConfig]);

  const totalPages = Math.ceil(sortedLanguages.length / ITEMS_PER_PAGE);
  const lignesAffichees = useMemo(() => {
    const debut = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedLanguages.slice(debut, debut + ITEMS_PER_PAGE);
  }, [sortedLanguages, currentPage]);

  // Changer de tri remet au début : rester en page 3 d'un autre classement n'a pas de sens.
  useEffect(() => { setCurrentPage(1); }, [sortConfig, languages]);

  const totals = useMemo(() => {
    // Le total d'une période ne dépend pas de la dimension : on affiche celui que GA4 a
    // calculé pour l'ensemble du site, jamais une addition des lignes reçues.
    if (serverTotals && serverTotals.sessions > 0) return serverTotals;

    if (!languages || !languages.length) return { sessions: 0, users: 0, pageViews: 0, bounceRate: 0, avgSessionDuration: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 };

    const result = languages.reduce((acc, language) => ({
      sessions: acc.sessions + (language.sessions || 0),
      users: acc.users + (language.users || 0),
      pageViews: acc.pageViews + (language.pageViews || 0),
      bounceSum: acc.bounceSum + ((language.bounceRate || 0) * (language.sessions || 0)),
      durationSum: acc.durationSum + ((language.avgSessionDuration || 0) * (language.sessions || 0)),
      conversions: acc.conversions + (language.conversions || 0),
      conversionsValue: acc.conversionsValue + (language.conversionsValue || 0),
      transactions: acc.transactions + (language.transactions || 0),
      totalRevenue: acc.totalRevenue + (language.totalRevenue || 0)
    }), { sessions: 0, users: 0, pageViews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;

    return result;
  }, [languages, serverTotals]);

  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousLanguages || !previousLanguages.length) return null;

    // Même règle que pour le total courant : la période de comparaison a elle aussi
    // son total calculé par GA4, sinon la variation compare deux choses différentes.
    if (serverPreviousTotals && serverPreviousTotals.sessions > 0) return serverPreviousTotals;

    const result = previousLanguages.reduce((acc, language) => ({
      sessions: acc.sessions + (language.sessions || 0),
      users: acc.users + (language.users || 0),
      pageViews: acc.pageViews + (language.pageViews || 0),
      bounceSum: acc.bounceSum + ((language.bounceRate || 0) * (language.sessions || 0)),
      durationSum: acc.durationSum + ((language.avgSessionDuration || 0) * (language.sessions || 0)),
      conversions: acc.conversions + (language.conversions || 0),
      conversionsValue: acc.conversionsValue + (language.conversionsValue || 0),
      transactions: acc.transactions + (language.transactions || 0),
      totalRevenue: acc.totalRevenue + (language.totalRevenue || 0)
    }), { sessions: 0, users: 0, pageViews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;

    return result;
  }, [compareEnabled, previousLanguages, serverPreviousTotals]);

  // Donnees pour le pie chart Sessions
  const sessionsChartData = useMemo(() => {
    if (!languages || !languages.length) return [];

    return languages.map(language => ({
      name: language.language,
      value: language.sessions || 0,
      percentage: totals.sessions > 0 ? ((language.sessions / totals.sessions) * 100).toFixed(1) : 0
    })).filter(item => parseFloat(item.percentage) > 0);
  }, [languages, totals.sessions]);

  // Donnees pour le pie chart Conversions
  const conversionsChartData = useMemo(() => {
    if (!languages || !languages.length) return [];

    return languages.map(language => ({
      name: language.language,
      value: language.conversions || 0,
      percentage: totals.conversions > 0 ? ((language.conversions / totals.conversions) * 100).toFixed(1) : 0
    })).filter(item => parseFloat(item.percentage) > 0);
  }, [languages, totals.conversions]);

  // Donnees pour le pie chart Transactions (e-commerce)
  const transactionsChartData = useMemo(() => {
    if (!languages || !languages.length) return [];

    return languages.map(language => ({
      name: language.language,
      value: language.transactions || 0,
      percentage: totals.transactions > 0 ? ((language.transactions / totals.transactions) * 100).toFixed(1) : 0
    })).filter(item => parseFloat(item.percentage) > 0);
  }, [languages, totals.transactions]);

  // Determine si on affiche Transactions ou Conversions dans le pie chart
  const showTransactionsPieChart = isMetricActive('transactions') && !isMetricActive('conv');
  const pieChartData = showTransactionsPieChart ? transactionsChartData : conversionsChartData;
  const pieChartLabel = showTransactionsPieChart
    ? 'Transactions'
    : libelleConversions(conversionConfig, conversionLabel);
  const pieChartTotal = showTransactionsPieChart ? totals.transactions : totals.conversions;

  // Mapping langue -> couleur
  const languageColorMap = useMemo(() => {
    const map = {};
    languages.forEach((language, index) => {
      map[language.language] = COLORS[index % COLORS.length];
    });
    return map;
  }, [languages]);

  const legendData = useMemo(() => {
    if (!languages || !languages.length) return [];

    return languages.map((language, index) => ({
      value: language.language,
      color: COLORS[index % COLORS.length]
    }));
  }, [languages]);

  // Dix étiquettes au plus : au-delà, la colonne s'étire sur plusieurs écrans et désigne des
  // parts invisibles. Le camembert et les totaux, eux, portent toujours sur tout.
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(legendData);

  const columnConfig = useMemo(() => ({
    visites: {
      key: 'sessions',
      label: 'Sessions',
      getValue: (item) => item.sessions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.sessions || 0
    },
    visiteurs: {
      key: 'users',
      label: 'Utilisateurs',
      getValue: (item) => item.users || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.users || 0
    },
    rebond: {
      key: 'bounceRate',
      label: '% de Rebond',
      getValue: (item) => item.bounceRate || 0,
      format: (val) => `${formatNumber(val, 1)}%`,
      getPrevValue: (prev) => prev?.bounceRate || 0,
      inverted: true
    },
    pages_vues: {
      key: 'pageViews',
      label: 'Pages vues',
      getValue: (item) => item.pageViews || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.pageViews || 0
    },
    taux_conv: {
      key: 'conversionRate',
      label: libelleTauxConversion(conversionConfig, conversionLabel),
      getValue: (item) => item.sessions > 0 ? (item.conversions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.conversions / prev.sessions * 100) : 0
    },
    conv: {
      key: 'conversions',
      label: libelleConversions(conversionConfig, conversionLabel),
      getValue: (item) => item.conversions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.conversions || 0
    },
    valeur_conv: {
      key: 'conversionsValue',
      label: 'Valeur Conv.',
      getValue: (item) => item.conversionsValue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.conversionsValue || 0
    },
    transactions: {
      key: 'transactions',
      label: 'Transactions',
      getValue: (item) => item.transactions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.transactions || 0
    },
    chiffre_affaires: {
      key: 'totalRevenue',
      label: 'Chiffre d\'affaires',
      getValue: (item) => item.totalRevenue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.totalRevenue || 0
    },
    taux_transaction: {
      key: 'transactionRate',
      label: '% Conversion (transactions)',
      getValue: (item) => item.sessions > 0 ? (item.transactions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.transactions / prev.sessions * 100) : 0
    },
    panier_moyen: {
      key: 'avgOrderValue',
      label: 'Panier moyen',
      getValue: (item) => item.transactions > 0 ? (item.totalRevenue || 0) / item.transactions : 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev && prev.transactions > 0 ? (prev.totalRevenue || 0) / prev.transactions : 0
    },
    duree_moy: {
      key: 'avgSessionDuration',
      label: 'Durée moyenne',
      getValue: (item) => item.avgSessionDuration || 0,
      format: (val) => {
        const mins = Math.floor(val / 60);
        const secs = Math.floor(val % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      },
      getPrevValue: (prev) => prev?.avgSessionDuration || 0
    },
    pages_visite: {
      key: 'pagesPerSession',
      label: 'Pages/Session',
      getValue: (item) => item.sessions > 0 ? (item.pageViews || 0) / item.sessions : 0,
      format: (val) => formatNumber(val, 1),
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.pageViews || 0) / prev.sessions : 0
    }
  }), [formatNumber, conversionLabel, conversionConfig]);

  if (loading) {
    return (
      <div className="campaigns-table-section">
        <div className="section-header">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Languages size={18} /> Langues</h4>
        </div>
        <div className="table-container">
          <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            Chargement des langues...
          </div>
        </div>
      </div>
    );
  }

  if (!languages || languages.length === 0) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Languages size={18} /> Langues</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les metriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Langues GA4"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs */}
          {sessionsChartData.length > 0 && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Pie chart Sessions (gauche) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par langue</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={sessionsChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {sessionsChartData.map((entry, index) => (
                          <Cell key={`sessions-cell-${index}`} fill={languageColorMap[entry.name] || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legende (centre) */}
                <div style={{
                  minWidth: '150px',
                  padding: '0 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  {legendeVisible.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      fontSize: '13px'
                    }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: item.color,
                        marginRight: '8px',
                        borderRadius: '2px'
                      }}></div>
                      <span>{item.value}</span>
                    </div>
                  ))}
                  {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                  {legendeReste > 0 && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      + {legendeReste} autres
                    </div>
                  )}
                </div>

                {/* Pie chart Conversions/Transactions (droite) */}
                {pieChartData.length > 0 && pieChartTotal > 0 && (
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{pieChartLabel} par langue</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`pie-cell-${index}`} fill={languageColorMap[entry.name] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ` ${pieChartLabel.toLowerCase()}`, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tableau detaille */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <table className="devices-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('language')} className="sortable" style={{ cursor: 'pointer' }}>
                    Langue {sortConfig.key === 'language' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {metricsOrder.map(metricKey => {
                    if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                    const col = columnConfig[metricKey];
                    return (
                      <th key={metricKey} onClick={() => handleSort(col.key)} className="sortable numeric" style={{ cursor: 'pointer' }}>
                        <span className="th-libelle">{col.label}</span> {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {lignesAffichees.map((language, index) => {
                  const prev = compareEnabled && previousLanguages
                    ? previousLanguages.find(p => p.language === language.language)
                    : null;

                  return (
                    <tr key={language.language || index}>
                      <td className="device-name" style={{ whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: languageColorMap[language.language] || COLORS[index % COLORS.length],
                          marginRight: '8px',
                          flexShrink: 0
                        }}></span>
                        {language.language}
                      </td>
                      {metricsOrder.map(metricKey => {
                        if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                        const col = columnConfig[metricKey];
                        const value = col.getValue(language);
                        const prevValue = prev ? col.getPrevValue(prev) : null;
                        const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_devices', accountId);
                        return (
                          <td key={metricKey} className={`numeric ${objectiveClass}`}>
                            {col.format(value)}
                            {prev && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  {metricsOrder.map(metricKey => {
                    if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                    const col = columnConfig[metricKey];
                    const value = col.getValue(totals);
                    const prevValue = previousTotals ? col.getPrevValue(previousTotals) : null;
                    const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_devices', accountId);
                    return (
                      <td key={metricKey} className={`numeric ${objectiveClass}`}>
                        <strong>{col.format(value)}</strong>
                        {previousTotals && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedLanguages.length)} sur {sortedLanguages.length} langues
                </div>
                <div className="pagination-buttons">
                  <button
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Precedent
                  </button>
                  <button
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de configuration des metriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ga4_devices"
        accountId={accountId}
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </>
  );
};

export default GA4LanguagesChart;
